import 'react-quill/dist/quill.snow.css';
import './CreateStory.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createStory, updateStory } from '../../../../services/storyService';

import BlotFormatter from 'quill-blot-formatter';
import CreateSroryPopup from './CreateSroryPopup';
import ReactQuill, { Quill } from 'react-quill';
import { selectUser } from '../../../../redux/reducers/userReducer';
import { uploadFileToS3 } from '../../../../services/uploadService';
import { useLoader } from '../../../../hooks/useLoader';
import { useStoryPresence } from '../../../../hooks/useStoryPresence';
import { useSelector } from 'react-redux';

// Drag-to-resize handles for images; writes width/height attributes onto the
// <img> tag so the chosen size persists in the saved HTML (web, email, archive).
Quill.register('modules/blotFormatter', BlotFormatter);

function CreateStory({
  story = null,
  backPress,
  allBriefings,
}: {
  story: any;
  backPress: Function;
  allBriefings: any[];
}) {
  const [headline, setHeadline] = useState(story ? story.heading : '');
  const [subHeading, setSubHeading] = useState(story ? story.subHeading : '');
  const [storyText, setStoryText] = useState(story ? story.content : '');
  const [showModal, setShowModal] = useState(false);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [bannerVideo, setBannerVideo] = useState<string | null>(null);
  const isForEdited = !!story?.id;

  const user = useSelector(selectUser);
  const { showLoading } = useLoader();
  const quillRef = useRef<ReactQuill>(null);

  // Soft-lock: announce that we're editing; shows a banner if someone else
  // already holds this story (new stories have no id — nothing to claim).
  const presenceHolder = useStoryPresence(
    story?.id,
    user.data?.user.user_id,
    user.data?.user.name || '',
    'editing'
  );
  const bulletsRef = useRef<HTMLTextAreaElement>(null);

  // Bullets only exist on the lead story, so the box sizes itself to its
  // content: one empty row for everything else, growing (capped) as bullets
  // are typed — the freed height goes to the body editor below.
  const BULLETS_MAX_HEIGHT = 240;
  const autoSizeBullets = useCallback(() => {
    const el = bulletsRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, BULLETS_MAX_HEIGHT)}px`;
    el.style.overflowY =
      el.scrollHeight > BULLETS_MAX_HEIGHT ? 'auto' : 'hidden';
  }, []);
  useEffect(() => {
    autoSizeBullets();
  }, [autoSizeBullets]);

  // Custom image handler: upload the picked image to S3 and insert its public
  // URL, instead of Quill's default base64 inlining (which bloats the story
  // document and gets stripped by email clients).
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        showLoading(true);
        const publicUrl = await uploadFileToS3(file, 'story-images');
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', publicUrl);
          quill.setSelection(range.index + 1, 0);
        }
      } catch (error) {
        console.error('Image upload failed', error);
        alert('Image upload failed. Please try again.');
      } finally {
        showLoading(false);
      }
    };
    input.click();
  }, [showLoading]);

  const quillModules = useMemo(
    () => ({
      blotFormatter: {},
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link'],
          ['blockquote'],
          [{ font: [] }],
          ['clean'],
          ['image'],
        ],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler]
  );

  const createOrUpdateStoryAPI = async (
    type: string = 'public',
    storyType: string = 'column',
    topics: string[],
    briefing?: any,
    guestAuthor?: { name: string; imageUrl: string } | null
  ) => {
    try {
      showLoading(true);
      if (story) {
        const data = await updateStory({
          storyId: story.id,
          email: user.data?.user.user_id || '',
          name: user.data?.user.name || '',
          imageUrl: bannerImage || '',
          heading: headline,
          subHeading: subHeading,
          content: storyText,
          status: story.status,
          briefingId: story.briefingId,
          expectedContentUpdatedAt: story.contentUpdatedAt?._seconds ?? null,
          briefingUrl: 'www.eurointelligence.com/briefings',
          twitter: 'www.eurointelligence.com/twitter',
          secondTwitter: 'www.eurointelligence.com/twitter1',
          topic: topics,
          type,
          storyType,
        });
      } else {
        const data = await createStory({
          email: user.data?.user.user_id || '',
          name: user.data?.user.name || '',
          imageUrl: bannerImage || '',
          heading: headline,
          subHeading: subHeading,
          content: storyText,
          status: 'editing',
          briefingId: briefing?.id || '',
          briefingUrl: 'www.eurointelligence.com/briefings',
          twitter: 'www.eurointelligence.com/twitter',
          secondTwitter: 'www.eurointelligence.com/twitter1',
          topic: topics,
          type,
          storyType,
          // Guest byline override (columns only) — the public author name and
          // photo; the email/name above still record who actually filed it.
          ...(guestAuthor
            ? {
                authorName: guestAuthor.name,
                authorImageUrl: guestAuthor.imageUrl,
              }
            : {}),
        });
      }
      showLoading(false);
      setShowModal(false);
      alert(
        story ? 'Story updated successfully!' : 'Story Created Successfully'
      );
      if (!story) {
        backPress();
      }
    } catch (error) {
      showLoading(false);
      if (error.response.data.message) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong");
      }
      console.log(error);
    }
  };

  const isBtnEnable = headline.length > 3 && storyText.length > 10;

  const handleBannerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerVideo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {presenceHolder && (
        <div
          style={{
            background: '#FFF4E5',
            border: '1px solid #F0B429',
            borderRadius: 6,
            color: '#8A5A00',
            fontFamily: 'Nunito Sans',
            fontSize: 14,
            fontWeight: 600,
            padding: '8px 14px',
            margin: '8px 16px 0px 16px',
          }}
        >
          ⚠️ {presenceHolder.name} is also in this story right now (
          {presenceHolder.activity}). If you both save, the second save will
          be rejected to protect the first one's changes.
        </div>
      )}
      <div className='header-container'>
        <h3
          onClick={backPress}
          style={{ textDecorationLine: 'underline', cursor: 'pointer' }}
        >
          {'Back'}
        </h3>
        <span>
          <button
            style={{
              padding: '8px 16px',
              background: isBtnEnable ? '#0B335E' : '#B3C0CD',
              color: '#FFFFFF',

              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
            }}
            className='save-a-Briefing'
            disabled={!isBtnEnable}
            onClick={() => {
              if (isForEdited) {
                createOrUpdateStoryAPI(
                  story.type,
                  story.storyType,
                  story.topics
                );
              } else {
                setShowModal(true);
              }
            }}
          >
            {isForEdited ? 'Save' : 'Submit for Review'}
          </button>
        </span>
      </div>

      <div className='createStory-mainContainer'>
        <div>
          <input
            type='text'
            value={headline}
            placeholder='Story Headline'
            style={{
              width: '100%',
              fontWeight: 400,
              fontFamily: 'Nunito Sans',
              fontSize: 40,
              marginTop: 5,
              borderWidth: 0,
              borderRadius: 2,
              color: '#161616',
            }}
            onChange={(e) => {
              setHeadline(e.target.value);
            }}
          />
        </div>
        <div>
          <textarea
            ref={bulletsRef}
            value={subHeading}
            placeholder='Bullet points (lead story only)'
            rows={1}
            style={{
              width: '99%',
              marginTop: 5,
              resize: 'none',
              borderWidth: 0,
              borderRadius: 2,
              fontWeight: 400,
              fontFamily: 'Nunito Sans',
              fontSize: 20,
              color: '#636363',
              border: 'none',
              paddingLeft: 12,
            }}
            onChange={(e) => {
              setSubHeading(e.target.value);
              autoSizeBullets();
            }}
          />
        </div>

        {false && (
          <div style={{ marginTop: 20 }}>
            <img
              src='./assets/Circle.svg'
              alt='circleImg'
              onClick={() => {
                setShowModal(false);
                backPress();
              }}
              style={{
                marginRight: 10,
                borderRadius: 100,
                color: '#949494',
              }}
            />

            <button
              style={{
                marginRight: 10,
                width: 160,
                height: 32,
                borderRadius: 100,
                border: '1px solid #B7B7B7',
                color: '#949494',
                fontSize: 12,
              }}
              onClick={() =>
                document.getElementById('bannerImageUpload')?.click()
              }
            >
              Add Banner Image
            </button>
            <input
              id='bannerImageUpload'
              type='file'
              accept='image/*'
              style={{ display: 'none' }}
              onChange={handleBannerImageChange}
            />
            <button
              style={{
                marginRight: 10,
                width: 160,
                height: 32,
                borderRadius: 100,
                border: '1px solid #B7B7B7',
                color: '#949494',
                fontSize: 12,
              }}
              onClick={() =>
                document.getElementById('bannerVideoUpload')?.click()
              }
            >
              Add Banner Video Clip
            </button>
            <input
              id='bannerVideoUpload'
              type='file'
              accept='video/*'
              style={{ display: 'none' }}
              onChange={handleBannerVideoChange}
            />
            <button
              style={{
                marginRight: 10,
                width: 160,
                height: 32,
                borderRadius: 100,
                border: '1px solid #B7B7B7',
                color: '#949494',
                fontSize: 12,
              }}
            >
              Use AI to Generate Image
            </button>
          </div>
        )}

        {bannerImage && (
          <div style={{ marginTop: 20 }}>
            <h4>Banner Image</h4>
            <img
              src={bannerImage}
              alt='Banner'
              style={{ width: '100%', maxHeight: 300 }}
            />
          </div>
        )}
        {bannerVideo && (
          <div style={{ marginTop: 20 }}>
            <h4>Banner Video</h4>
            <video
              src={bannerVideo}
              controls
              style={{ width: '100%', maxHeight: 300 }}
            />
          </div>
        )}
        <div className='createStory-editor' style={{ width: '100%' }}>
          <ReactQuill
            ref={quillRef}
            defaultValue={story ? story.content : ''}
            theme='snow'
            onChange={setStoryText}
            placeholder='Tell your story...'
            modules={quillModules}
            style={{
              fontSize: 20,
              fontFamily: 'Nunito Sans',
              fontWeight: 400,
            }}
          />
        </div>

        {showModal && (
          <CreateSroryPopup
            closePopupClick={() => {
              setShowModal(false);
            }}
            submitBtnClick={(
              type: string,
              postAs: string,
              topics: string[],
              briefing: any,
              guestAuthor: { name: string; imageUrl: string } | null
            ) => {
              createOrUpdateStoryAPI(type, postAs, topics, briefing, guestAuthor);
            }}
            allBriefings={allBriefings}
            heading={headline}
            subHeading={subHeading}
            canSetGuestAuthor={user.data?.user.userType === 'admin'}
          />
        )}
      </div>
    </div>
  );
}

export default CreateStory;
